import { useMemo, useState } from "react";
import { Money, QrCode, SpinnerGap, UploadSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";

const MONTH_ID = ["", "Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

export function formatMonthLabel(period) {
    const [year, month] = (period || "").split("-");
    return `${MONTH_ID[Number(month)] || month} ${year}`;
}

export function formatRupiahValue(value) {
    return `Rp ${Number(value || 0).toLocaleString("id-ID")}`;
}

export default function PaymentDialog({ open, onClose, child, paymentInfo, type, onSubmitted }) {
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [proofUrl, setProofUrl] = useState("");
    const [note, setNote] = useState("");
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [method, setMethod] = useState("midtrans");

    const isRegistration = type === "registration";
    const midtransAvailable = !!paymentInfo?.midtrans_enabled;
    const effectiveMethod = midtransAvailable ? method : "manual";
    const unpaidMonths = useMemo(() => (child?.months || []).filter((m) => m.status === "unpaid"), [child]);
    const total = isRegistration
        ? child?.first_payment_total || 0
        : selectedMonths.length * (child?.monthly_amount || 0);

    const reset = () => {
        setSelectedMonths([]);
        setProofUrl("");
        setNote("");
        setUploading(false);
        setSubmitting(false);
        setMethod("midtrans");
    };

    const handleOpenChange = (value) => {
        if (!value) {
            reset();
            onClose();
        }
    };

    const toggleMonth = (month) => {
        setSelectedMonths((prev) => (prev.includes(month) ? prev.filter((m) => m !== month) : [...prev, month]));
    };

    const uploadProof = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/parent/payments/proof-upload", fd);
            setProofUrl(r.data.url);
            toast.success("Bukti pembayaran terunggah");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setUploading(false);
        }
    };

    const submit = async () => {
        if (!isRegistration && selectedMonths.length === 0) {
            toast.error("Pilih minimal satu bulan iuran");
            return;
        }
        if (effectiveMethod === "manual" && !proofUrl) {
            toast.error("Unggah bukti pembayaran dulu");
            return;
        }
        setSubmitting(true);
        try {
            if (effectiveMethod === "midtrans") {
                const r = await api.post("/parent/payments/midtrans", {
                    child_id: child.child_id,
                    type,
                    months: isRegistration ? [] : selectedMonths,
                });
                toast.success("Mengalihkan ke halaman pembayaran online...");
                window.location.assign(r.data.redirect_url);
                return;
            }
            await api.post("/parent/payments", {
                child_id: child.child_id,
                type,
                months: isRegistration ? [] : selectedMonths,
                proof_url: proofUrl,
                note,
            });
            toast.success("Pengajuan pembayaran terkirim. Menunggu konfirmasi admin/keuangan.");
            reset();
            onClose();
            onSubmitted?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="payment-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display uppercase text-xl flex items-center gap-2">
                        <Money size={20} className="text-racing-red" /> {isRegistration ? "Pembayaran Pendaftaran" : "Bayar Iuran"}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-racing-muted">
                        Atlet: <span className="font-medium text-neutral-900">{child?.name}</span>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 mt-2">
                    {isRegistration ? (
                        <div className="border border-racing-line bg-racing-surface p-4 text-sm space-y-1.5" data-testid="payment-registration-breakdown">
                            <div className="flex justify-between"><span>Biaya pendaftaran siswa baru</span><span className="font-medium">{formatRupiahValue(child?.registration_fee)}</span></div>
                            <div className="flex justify-between"><span>Iuran bulan pertama ({formatMonthLabel(child?.first_month)})</span><span className="font-medium">{formatRupiahValue(child?.monthly_amount)}</span></div>
                        </div>
                    ) : (
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Pilih Bulan Iuran ({formatRupiahValue(child?.monthly_amount)}/bulan)</p>
                            <div className="flex flex-wrap gap-2" data-testid="payment-month-list">
                                {unpaidMonths.length === 0 && <p className="text-sm text-racing-muted">Semua iuran yang sudah bisa dibayar sudah lunas.</p>}
                                {unpaidMonths.map((m) => (
                                    <button
                                        key={m.month}
                                        type="button"
                                        onClick={() => toggleMonth(m.month)}
                                        data-testid={`payment-month-${m.month}`}
                                        className={`px-4 py-2.5 text-xs uppercase tracking-[0.15em] border transition-colors ${
                                            selectedMonths.includes(m.month)
                                                ? "bg-racing-red border-racing-red text-white"
                                                : "bg-white border-racing-line text-racing-muted hover:border-racing-red hover:text-racing-red"
                                        }`}
                                    >
                                        {formatMonthLabel(m.month)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {midtransAvailable && (
                        <div data-testid="payment-method-selector">
                            <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Cara Bayar</p>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => setMethod("midtrans")}
                                    data-testid="payment-method-midtrans"
                                    className={`px-3 py-3.5 text-[10px] uppercase tracking-[0.15em] border transition-colors text-left ${
                                        method === "midtrans" ? "bg-racing-red border-racing-red text-white" : "bg-white border-racing-line text-racing-muted hover:border-racing-red"
                                    }`}
                                >
                                    <span className="block font-medium">Online Otomatis</span>
                                    <span className={`block mt-1 normal-case tracking-normal text-[11px] ${method === "midtrans" ? "text-white/80" : "text-racing-muted"}`}>QRIS, VA bank, GoPay/OVO/dst — langsung lunas</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMethod("manual")}
                                    data-testid="payment-method-manual"
                                    className={`px-3 py-3.5 text-[10px] uppercase tracking-[0.15em] border transition-colors text-left ${
                                        method === "manual" ? "bg-racing-red border-racing-red text-white" : "bg-white border-racing-line text-racing-muted hover:border-racing-red"
                                    }`}
                                >
                                    <span className="block font-medium">Manual ShopeePay</span>
                                    <span className={`block mt-1 normal-case tracking-normal text-[11px] ${method === "manual" ? "text-white/80" : "text-racing-muted"}`}>Transfer + unggah bukti, admin konfirmasi</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {effectiveMethod === "midtrans" ? (
                        <div className="border-2 border-racing-gold/60 bg-racing-gold/5 p-4" data-testid="payment-instructions">
                            <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-racing-golddeep mb-3"><QrCode size={16} /> Pembayaran Online</p>
                            <ol className="text-sm text-neutral-900 space-y-1.5 list-decimal list-inside">
                                <li>Klik tombol bayar di bawah — Anda akan diarahkan ke halaman pembayaran aman Midtrans</li>
                                <li>Pilih QRIS, transfer bank (VA), atau e-wallet</li>
                                <li>Nominal: <span className="font-display text-racing-red" data-testid="payment-total">{formatRupiahValue(total)}</span></li>
                                <li>Selesai bayar, status otomatis lunas + kwitansi & WhatsApp terkirim tanpa menunggu admin</li>
                            </ol>
                        </div>
                    ) : (
                        <div className="border-2 border-racing-gold/60 bg-racing-gold/5 p-4" data-testid="payment-instructions">
                            <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-racing-golddeep mb-3"><QrCode size={16} /> Cara Membayar</p>
                            {!midtransAvailable && (
                                <p className="mb-3 text-[11px] border border-racing-line bg-white px-3 py-2 text-racing-muted" data-testid="payment-midtrans-off-note">Pembayaran online otomatis sementara dinonaktifkan — silakan bayar via QRIS lalu unggah bukti.</p>
                            )}
                            <ol className="text-sm text-neutral-900 space-y-1.5 list-decimal list-inside">
                                <li>Buka aplikasi ShopeePay / mobile banking / e-wallet apa pun</li>
                                <li>Transfer atau scan QRIS ke ShopeePay klub: <span className="font-display text-racing-red" data-testid="payment-shopeepay-number">{paymentInfo?.shopeepay_number || "-"}</span></li>
                                <li>Nominal: <span className="font-display text-racing-red" data-testid="payment-total">{formatRupiahValue(total)}</span></li>
                                <li>Unggah foto bukti pembayaran di bawah ini</li>
                            </ol>
                            {paymentInfo?.qris_image && (
                                <img src={fileUrl(paymentInfo.qris_image)} alt="QRIS ShopeePay Klub" className="mt-3 max-h-64 border border-racing-line bg-white object-contain" data-testid="payment-qris-image" />
                            )}
                        </div>
                    )}

                    {effectiveMethod === "manual" && (
                        <>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Bukti Pembayaran (wajib)</label>
                                {proofUrl ? (
                                    <div className="flex items-center gap-3 border border-green-600/40 bg-green-50 p-3" data-testid="payment-proof-preview">
                                        <CheckCircle size={20} className="text-green-700 shrink-0" />
                                        <img src={fileUrl(proofUrl)} alt="Bukti pembayaran" className="h-16 border border-racing-line object-contain bg-white" />
                                        <button type="button" onClick={() => setProofUrl("")} className="text-[10px] uppercase tracking-[0.2em] text-racing-red" data-testid="payment-proof-remove">Ganti</button>
                                    </div>
                                ) : (
                                    <label className="flex items-center justify-center gap-2 border-2 border-dashed border-racing-line hover:border-racing-red p-5 cursor-pointer text-xs uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red transition-colors" data-testid="payment-proof-dropzone">
                                        {uploading ? <SpinnerGap size={18} className="animate-spin" /> : <UploadSimple size={18} />}
                                        {uploading ? "Mengunggah..." : "Pilih Foto Bukti Bayar"}
                                        <input type="file" accept="image/*" className="hidden" data-testid="payment-proof-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadProof(f); }} />
                                    </label>
                                )}
                            </div>

                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Catatan (opsional)</label>
                                <input type="text" value={note} onChange={(e) => setNote(e.target.value)} data-testid="payment-note-input" className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" placeholder="mis. bayar atas nama Ayah Agis" />
                            </div>
                        </>
                    )}

                    <button
                        type="button"
                        onClick={submit}
                        disabled={submitting || uploading || total <= 0}
                        data-testid="payment-submit"
                        className="w-full bg-racing-red hover:bg-racing-redhover disabled:opacity-60 text-white text-xs uppercase tracking-[0.25em] py-4 transition-colors duration-300 flex items-center justify-center gap-2"
                    >
                        {submitting && <SpinnerGap size={16} className="animate-spin" />}
                        {effectiveMethod === "midtrans"
                            ? `Lanjut ke Pembayaran Online ${total > 0 ? `• ${formatRupiahValue(total)}` : ""}`
                            : `Kirim Bukti Pembayaran ${total > 0 ? `• ${formatRupiahValue(total)}` : ""}`}
                    </button>
                    <p className="text-[11px] text-racing-muted text-center">
                        {effectiveMethod === "midtrans"
                            ? "Pembayaran online diproses otomatis — lunas seketika setelah Anda membayar."
                            : "Admin/keuangan akan memeriksa bukti Anda. Setelah disetujui, status otomatis lunas dan kwitansi + WhatsApp terkirim."}
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
}
